import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let initialSessionResolved = false;

    // Set up auth listener BEFORE getSession (Supabase best practice)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        // Only update state after the initial session has been resolved
        // to avoid a race condition where onAuthStateChange fires with
        // null before getSession completes, causing a false redirect.
        if (!initialSessionResolved && event === "INITIAL_SESSION") {
          initialSessionResolved = true;
          setSession(currentSession);
          setUser(currentSession?.user ?? null);
          setLoading(false);
          return;
        }

        if (initialSessionResolved) {
          setSession(currentSession);
          setUser(currentSession?.user ?? null);
          // Don't set loading here — it's already false
        }
      }
    );

    // Fallback: if INITIAL_SESSION event never fires (older SDK versions),
    // resolve from getSession after a short delay.
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      if (!initialSessionResolved) {
        initialSessionResolved = true;
        setSession(s);
        setUser(s?.user ?? null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}
